import {
  Component,
  HostListener,
  inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { ButtonsModule, IconModule } from 'axis-components';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Logger } from '../../_core/logger.service';
import { HeaderSharedService } from '../../_services/header-shared.service';
import { PdfService } from '../../_services/pdf/pdf.service';
import { RequestService } from '../../_services/request/request.service';
import { FormType } from '../../config/constants';
import { ModalPIPReviewFormComponent } from '../modal-pip-review-form/modal-pip-review-form.component';
import { ModalUIMUIReviewFormComponent } from '../modal-uimui-review-form/modal-uimui-review-form.component';
import { PipBaseRulesService } from '../../state-disclosures/pip/services/pip-base-rules.service';
import { UimuiBaseRulesService } from '../../state-disclosures/uimui/services/uimui-base-rules.service';

interface Step {
  id: string;
  heading: string;
  flowId: string;
  substeps: SubStep[];
  active: boolean;
  completed: boolean;
  disabled?: boolean;
}

interface SubStep {
  id: string;
  heading: string;
  route: string;
  flowId: string;
  active: boolean;
  completed: boolean;
  disabled?: boolean;
}

@Component({
  standalone: true,
  selector: 'aside-consent',
  styleUrl: './aside-consent.component.scss',
  templateUrl: './aside-consent.component.html',
  imports: [
    ModalPIPReviewFormComponent,
    ModalUIMUIReviewFormComponent,
    ButtonsModule,
    IconModule,
  ],
})
export class AsideConsentComponent implements OnInit, OnDestroy {
  downloadUIUMFormsInProgress = false;
  downloadPipFormsInProgress = false;
  @Input() consentSelection: string = '';
  @Input() activeStepSet: string = 'digital';
  currentSteps: Step[] = [];
  leftSidebarClosed = false;
  private subscription = new Subscription();

  @ViewChild('modalPIPReviewForm')
  modalPIPReviewForm!: ModalPIPReviewFormComponent;
  @ViewChild('modalUIMUIReviewForm')
  modalUIMUIReviewForm!: ModalUIMUIReviewFormComponent;

  formTypeList = [];
  isUIUMEnabled = false;
  isPIPEnabled = false;

  constructor(
    public router: Router,
    public headerSharedService: HeaderSharedService,
    private requestService: RequestService,
    private pdfService: PdfService,
    private pipBaseRulesService: PipBaseRulesService,
    private uimuiBaseRulesService: UimuiBaseRulesService,
  ) {
    if (
      this.router.url.includes('pip-outstanding-state') ||
      this.router.url.includes('pip-signature')
    ) {
      if (Object.keys(this.pipBaseRulesService.pdfDataDump).length === 0) {
        this.router.navigate(['/home'], { queryParamsHandling: 'preserve' });
      }
    }
    if (
      this.router.url.includes('uimui-outstanding-state') ||
      this.router.url.includes('uimui-signature')
    ) {
      if (Object.keys(this.uimuiBaseRulesService.pdfDataDump).length === 0) {
        this.router.navigate(['/home'], { queryParamsHandling: 'preserve' });
      }
    }
  }
  @HostListener('window:resize')
  onResize(): void {
    this.leftSidebarClosed = false;
  }

  ngOnInit(): void {
    this.formTypeList = this.requestService.requestFormTypeList;
    this.isUIUMEnabled = this.formTypeList.includes(FormType.UM_UIM);
    this.isPIPEnabled = this.formTypeList.includes(FormType.PIP);
    this.subscription.add(
      this.headerSharedService.steps$.subscribe((steps) => {
        this.currentSteps = steps;
      }),
    );

    this.subscription.add(
      this.router.events
        .pipe(filter((e) => e instanceof NavigationEnd))
        .subscribe((e: NavigationEnd) => {
          this.headerSharedService.updateStepState(e.urlAfterRedirects);
        }),
    );

    this.headerSharedService.updateStepState(this.router.url);
  }
  isDigitalFlow(): boolean {
    return !this.router.url.includes('non-digital');
  }
  reviewUMUIMStateForms() {
    Logger.log('Start of review UM UIM State forms');
    const isDigital = this.isDigitalFlow();
    this.modalUIMUIReviewForm!.electronicConsent = isDigital;
    this.modalUIMUIReviewForm!.open();
    // this.getReviewStateForm(FormType.UM_UIM);
  }

  reviewPIPStateForms() {
    Logger.log('Start of review PIP State forms');
    const isDigital = this.isDigitalFlow();
    this.modalPIPReviewForm!.electronicConsent = isDigital;
    this.modalPIPReviewForm!.open();
  }

  step_clickHandler(substep: SubStep) {
    if (substep.completed) {
      const currentUrl = this.router.url;
      const isOnSummaryPage = currentUrl.includes('review-state');
      const isNavigatingToOutstanding =
        substep.route.includes('outstanding-state');

      // Check if navigating from summary to outstanding page
      if (isOnSummaryPage && isNavigatingToOutstanding) {
        // Determine which form type we're dealing with
        const isUIMUI = currentUrl.includes('uimui');
        const isPIP = currentUrl.includes('pip');

        // Validate the summary form before allowing navigation
        let canNavigate = true;
        if (isUIMUI) {
          canNavigate = this.uimuiBaseRulesService.canNavigateFromSummary();
        } else if (isPIP) {
          canNavigate = this.pipBaseRulesService.canNavigateFromSummary();
        }

        // If validation fails, prevent navigation
        if (!canNavigate) {
          return;
        }
      }

      this.saveCurrentPageDataViaSubscription();
      this.pipBaseRulesService.isContinueButtonClicked = false;
      this.uimuiBaseRulesService.isContinueButtonClicked = false;
      Logger.log('Navigating to ', substep.route);
      this.router.navigate([`/insured/${substep.route}`], {
        queryParamsHandling: 'preserve',
      });
    }
  }
  private saveCurrentPageDataViaSubscription(): void {
    const currentUrl = this.router.url;
    if (currentUrl.includes('uimui-outstanding-state')) {
      this.uimuiBaseRulesService.triggerDataSave();
    }
    if (currentUrl.includes('pip-outstanding-state')) {
      this.pipBaseRulesService.triggerDataSave();
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
